import * as RadixAvatar from '@radix-ui/react-avatar'
import React, { useMemo, useState } from 'react'
import { usePreviousDistinct } from 'react-use'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { NotFoundError } from '@root/shared/core/Error/Error'
import { type ClassNameProps } from '@root/shared/react'

import { IconProcessing } from '../../atoms'

import { CurrencyIconGoogleLens } from './currency-icon-google-lens'

import type { CurrencyModel } from '../../model'
import type { UnknownError } from '@root/shared/core/Error/Error'

type Props = {
  currency: CurrencyModel
  state: AsyncStateN.AsyncStateN<
    UnknownError | NotFoundError | IconProcessing,
    string | undefined
  >
  withGoogleLens?: boolean
  renderNotFound?: () => React.ReactNode
} & ClassNameProps &
  React.HTMLAttributes<HTMLElement>

export const CurrencyIconMinimal = ({
  currency,
  className,
  state,
  withGoogleLens = true,
  renderNotFound,
  ...divProps
}: Props) => {
  const [isHovered, setIsHovered] = useState(false)

  const prevState = usePreviousDistinct(state)

  const shouldRenderNotFound = useMemo(() => {
    const stateNotFound = pipe(
      state,
      AsyncStateN.match({
        Error: (error) => error instanceof NotFoundError,
        Success: (value) => !value,
        Loading: () => false,
        NotInitiated: () => false,
      }),
    )

    if (stateNotFound) {
      return true
    }

    if (!prevState) {
      return false
    }

    const prevStateNotFound = pipe(
      prevState,
      AsyncStateN.match({
        Error: (error) => error instanceof NotFoundError,
        Success: (value) => !value,
        Loading: () => false,
        NotInitiated: () => false,
      }),
    )
    return !AsyncStateN.isSuccess(state) && prevStateNotFound
  }, [state, prevState])

  if (renderNotFound && shouldRenderNotFound) {
    return renderNotFound()
  }

  // If icon moderation is processing, show loading
  if (AsyncStateN.isError(state) && state.error instanceof IconProcessing) {
    return <Root data-loaded={false} className={className} {...divProps} />
  }

  return (
    <Root
      data-loaded={AsyncStateN.isSuccess(state)}
      className={className}
      {...divProps}
      onPointerEnter={(e) => {
        setIsHovered(true)
        divProps.onPointerEnter?.(e)
      }}
      onPointerLeave={(e) => {
        setIsHovered(false)
        divProps.onPointerLeave?.(e)
      }}
    >
      <Image src={state.value} alt={currency.title} loading="lazy" />
      {isHovered && state.value && withGoogleLens ? (
        <CurrencyIconGoogleLens iconSrc={state.value} />
      ) : undefined}
    </Root>
  )
}

const Root = styled(RadixAvatar.Root)`
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  overflow: hidden;
  background: var(--backgroundA-2);
`

const Image = styled(RadixAvatar.Image)`
  width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: cover;
  border-radius: inherit;
  pointer-events: none;
  transition: opacity ease 0.15s;
  opacity: 0;
  display: block;

  ${Root}[data-loaded='true'] & {
    opacity: 1;
  }
`
